<template>
  <!-- Store Selection Screen -->
  <div v-if="!selectedStore"
    class="relative w-full flex-col items-center text-white bg-[#120F14] mt-[20px] pt-[100px] pb-[80px] px-4">
    <!-- Header -->
    <div class="flex flex-col justify-center items-center w-full mb-10">
      <img src="@/assets/maherco-store.avif" alt="Maherco Store Logo" class="w-[300px] sm:w-[360px] mb-6" />
      {{ $t('Store.SelectStore') }}
    </div>

    <!-- Store Option Container -->
    <div
      class="flex flex-col sm:flex-row justify-center items-center gap-8 sm:gap-20 w-full max-w-[920px] mx-auto py-10 px-6 rounded-2xl bg-[#140133]/90 border border-[#4fc3f7] shadow-[0_0_20px_#4fc3f7aa] backdrop-blur-md">
      <!-- Kick Store -->
      <img src="@/assets/maherco-kick-store.avif" alt="Kick Store"
        class="w-[340px] cursor-pointer hover:scale-105 transition-all duration-300" @click="chooseStore('kick')" />

      <!-- 21.com Store -->
      <img src="@/assets/maherco-21com-store.avif" alt="21.com Store"
        class="w-[340px] cursor-pointer hover:scale-105 transition-all duration-300" @click="chooseStore('21.com')" />
    </div>
  </div>

  <div v-if="selectedStore" class="relative w-full flex-col items-center text-white bg-[#120F14] mt-[70px] pb-[80px]">
    <!-- ===== HERO BANNER ===== -->
    <div class="relative w-full max-w-[1400px] mx-auto px-4 lg:px-8 xl:px-10 pt-6">
      <div class="relative w-full rounded-[16px] overflow-hidden min-h-[180px] sm:min-h-[200px]">
        <!-- Background Image -->
        <img
          :src="selectedStore === 'kick' ? require('@/assets/revamp/kick-store-header.png') : require('@/assets/revamp/21com-store-header.png')"
          alt="Store Header" class="absolute inset-0 w-full h-full object-cover" />
        <!-- Gradient Overlay -->
        <div class="absolute inset-0 bg-gradient-to-r from-[#120F14]/90 via-[#120F14]/60 to-transparent"></div>

        <!-- Content -->
        <div class="relative z-10 flex flex-col xl:flex-row items-center justify-between gap-6 p-6 sm:p-8 xl:p-10">
          <!-- Left: Title + Subtitle -->
          <div class="flex flex-col items-center xl:items-start text-center xl:text-left w-full xl:w-auto">
            <h1
              class="flex items-center justify-center xl:justify-start text-3xl sm:text-4xl xl:text-5xl font-extrabold font-oxanium tracking-tight">
              <template v-if="selectedStore === 'kick'">
                <img src="@/assets/revamp/kick-logo.png" alt="Kick" class="h-[0.7em] object-contain mr-3" />
              </template>
              <template v-else>
                <img src="@/assets/revamp/21com-logo.png" alt="21.com" class="h-[0.8em] object-contain mr-3" />
              </template>
              <span class="text-white">{{ $t('Store.StoreCaps') }}</span>
            </h1>
            <p class="text-[#B9CACB] text-sm sm:text-base mt-3 max-w-md">
              {{ $t('Store.StoreDescription') }}
            </p>
          </div>

          <!-- Right: Status Badges -->
          <div class="flex flex-col xl:flex-row items-center gap-4 flex-shrink-0 w-full xl:w-auto">
            <!-- Available Items Badge -->
            <div
              class="flex items-center gap-4 bg-[#14181F] border-l-[6px] border-l-[#beff2e] rounded-[8px] pl-4 pr-8 py-3 shadow-[0_4px_15px_rgba(0,0,0,0.3)] w-full max-w-[320px] xl:w-auto xl:max-w-none">
              <!-- Icon Container -->
              <div class="w-[50px] h-[50px] rounded-full bg-[#1B252E] flex items-center justify-center">
                <svg class="w-6 h-6 text-[#beff2e]" fill="none" stroke="currentColor" stroke-width="2"
                  viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                </svg>
              </div>

              <!-- Text Content -->
              <div class="flex flex-col">
                <span class="text-[11px] font-bold text-gray-300 uppercase tracking-[0.15em]">{{
                  $t('Store.AvailableItems')
                }}</span>
                <div class="flex items-baseline gap-1.5 -mt-1">
                  <!-- Spinner when loading -->
                  <span v-if="loading">
                    <div class="twist-spinner-small mt-2"></div>
                  </span>
                  <!-- Value -->
                  <span v-else class="text-[28px] font-extrabold text-[#beff2e] font-oxanium leading-none mt-1">{{
                    animatedTotalItems
                  }}</span>
                  <span class="text-[#beff2e] text-sm font-bold ml-1">{{ $t('Store.Items') }}</span>
                </div>
              </div>
            </div>

            <!-- Current Balance Badge -->
            <div
              class="flex items-center gap-4 bg-[#14181F] border-l-[6px] border-l-[#00FFFF] rounded-[8px] pl-4 pr-8 py-3 shadow-[0_4px_15px_rgba(0,0,0,0.3)] w-full max-w-[320px] xl:w-auto xl:max-w-none">
              <!-- Icon Container -->
              <div class="w-[50px] h-[50px] rounded-full bg-[#1B252E] flex items-center justify-center">
                <img src="@/assets/revamp/icons/points.png" alt="Points"
                  class="w-6 h-6 object-contain brightness-0 invert opacity-80"
                  style="filter: drop-shadow(0 0 5px rgba(0, 255, 255, 0.5));" />
              </div>

              <!-- Text Content -->
              <div class="flex flex-col">
                <span class="text-[11px] font-bold text-gray-300 uppercase tracking-[0.15em]">{{
                  $t('Store.CurrentBalance')
                }}</span>
                <div class="flex items-baseline gap-1.5 -mt-1">
                  <!-- Spinner when loading -->
                  <span v-if="loading || animatedPoints === null">
                    <div class="twist-spinner-small mt-2"></div>
                  </span>
                  <!-- Value -->
                  <span v-else class="text-[28px] font-extrabold text-[#00FFFF] font-oxanium leading-none mt-1">{{
                    animatedPoints
                  }}</span>
                  <span class="text-[#00FFFF] text-sm font-bold ml-1">{{ $t('Store.Pts') }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- ===== MAIN CONTENT AREA ===== -->
    <div class="w-full max-w-[1400px] mx-auto px-4 lg:px-8 xl:px-10 mt-6">

      <!-- Controls Row -->
      <div
        class="flex flex-col sm:flex-row flex-wrap items-stretch sm:items-center justify-start lg:justify-end gap-3 mb-8 w-full mt-4 lg:mt-0">
        <!-- Search Bar -->
        <div
          class="relative flex items-center bg-[#1A1D24] border border-gray-700/50 rounded-[10px] px-4 h-[44px] w-full sm:w-auto sm:min-w-[220px]">
          <svg class="w-4 h-4 text-[#B9CACB] mr-2" fill="none" stroke="currentColor" stroke-width="2"
            viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round"
              d="M21 21l-4.35-4.35m2.35-6.65a7 7 0 1 1-14 0 7 7 0 0 1 14 0z" />
          </svg>
          <input type="text" v-model="searchQuery" :placeholder="$t('Store.SearchPlaceholder')"
            class="bg-transparent text-sm text-white placeholder-[#B9CACB] focus:outline-none w-full" />
        </div>

        <!-- Sort Dropdown -->
        <div class="relative h-[44px] w-full sm:w-auto sm:flex-1 lg:flex-none">
          <div @click="toggleSortDropdown"
            class="flex items-center justify-between h-full bg-[#1A1D24] text-sm w-full sm:min-w-[200px] border border-gray-700/50 rounded-[10px] px-4 cursor-pointer">
            <span class="text-[#B9CACB] whitespace-nowrap">{{ selectedSortOption.label }}</span>
            <svg class="w-4 h-4 text-[#B9CACB] ml-2 flex-shrink-0 transition-transform"
              :class="{ 'rotate-180': showSortDropdown }" fill="none" stroke="currentColor" stroke-width="2"
              viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" d="M19 9l-7 7-7-7" />
            </svg>
          </div>
          <transition name="fade">
            <div v-if="showSortDropdown"
              class="absolute top-12 left-0 bg-[#1A1D24] border border-gray-700/50 rounded-[10px] shadow-lg w-full z-50 overflow-hidden">
              <div v-for="option in sortOptions" :key="option.value" @click="applySort(option)"
                class="px-4 py-3 hover:bg-white/5 cursor-pointer transition-all text-sm" :class="{
                  'text-[#00F0FF] bg-white/5': option.value === selectedSortOption.value,
                  'text-[#B9CACB]': option.value !== selectedSortOption.value,
                }">
                {{ option.label }}
              </div>
            </div>
          </transition>
        </div>

        <!-- Buttons Group -->
        <div class="flex items-center gap-3 w-full sm:w-auto">
          <!-- Purchase History Button -->
          <button @click="fetchTransactions"
            class="h-[44px] px-5 flex-1 sm:flex-none bg-[#00F0FF] text-black font-bold text-sm rounded-[10px] hover:bg-[#00d4e0] transition-colors duration-200 whitespace-nowrap flex-shrink-0">
            {{ $t("Store.PurchaseHistory") }}
          </button>
          <button @click="refreshPage"
            class="h-[44px] w-[44px] flex-shrink-0 bg-[#1A1D24] border border-gray-700/50 rounded-[10px] flex items-center justify-center hover:bg-white/5 transition-colors duration-200"
            title="Refresh">
            <svg class="w-4 h-4 text-[#B9CACB]" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round"
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          </button>
        </div>
      </div>

      <!-- Loading Spinner -->
      <div v-if="loading" class="flex justify-center items-center min-h-[400px] w-full">
        <div class="twist-spinner"></div>
      </div>

      <!-- ===== PRODUCT CARDS GRID ===== -->
      <div v-else>
        <div class="grid gap-5 sm:gap-6" style="grid-template-columns: repeat(auto-fill, minmax(260px, 1fr))">
          <div v-for="item in paginatedProducts" :key="item._id" @click="goToItemPage(item._id, selectedStore)"
            class="group relative bg-[#1A1D24] rounded-[14px] border border-gray-800/40 overflow-hidden cursor-pointer transition-all duration-300 hover:border-[#2EBBF0]/40 hover:shadow-[0_0_20px_rgba(46,187,240,0.1)]">
            <!-- Image Container -->
            <div class="relative w-full aspect-[4/3] bg-[#0D0B10] overflow-hidden">
              <img :src="item.imageUrl || require('@/assets/store-fallback.png')" :alt="item.title"
                class="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
              <!-- Status Badge -->
              <span v-if="item.quantity > 0"
                class="absolute top-3 left-3 text-[10px] font-bold uppercase tracking-wider px-3 py-1 rounded-full"
                :class="item.quantity <= 3 ? 'bg-red-500/90 text-white' : 'bg-[#00F0FF]/90 text-black'">
                {{ item.quantity <= 3 ? $t('Store.Limited') : $t('Store.InStock') }} </span>
                  <span v-else
                    class="absolute top-3 left-3 text-[10px] font-bold uppercase tracking-wider px-3 py-1 rounded-full bg-gray-600/90 text-white">
                    {{ $t('Store.SoldOut') }}
                  </span>
            </div>

            <!-- Card Content -->
            <div class="p-4 flex flex-col gap-3">
              <!-- Title -->
              <p class="text-white font-bold text-sm truncate" :title="item.title">
                {{ item.title }}
              </p>

              <div class="flex flex-col gap-2 mt-1">
                <!-- Price -->
                <div class="flex items-center gap-2">
                  <svg class="w-4 h-4 text-[#00F0FF]" fill="none" stroke="currentColor" stroke-width="2"
                    viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round"
                      d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                  </svg>
                  <p class="font-extrabold text-base font-oxanium leading-none">
                    <span class="text-[#00F0FF]">{{ (item.requiredPoints || 0).toLocaleString() }}</span>
                    <span class="text-[#B9CACB] text-[10px] ml-1 font-bold">{{ $t('Store.Pts') }}</span>
                  </p>
                </div>

                <!-- Quantity -->
                <div class="flex items-center gap-2">
                  <svg class="w-4 h-4 text-[#B9CACB]" fill="none" stroke="currentColor" stroke-width="2"
                    viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round"
                      d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                  </svg>
                  <p class="text-[#B9CACB] text-xs font-semibold">
                    {{ item.quantity || 0 }} {{ $t('Store.Left') }}
                  </p>
                </div>
              </div>

              <!-- Redeem Button -->
              <button
                class="mt-2 w-full py-2.5 bg-transparent border border-[#00F0FF]/50 text-[#00F0FF] font-bold text-xs rounded-[10px] uppercase tracking-wider transition-all duration-300 hover:bg-[#00F0FF] hover:text-black"
                @click.stop="goToItemPage(item._id, selectedStore)">
                {{ $t('Store.RedeemNow') }}
              </button>
            </div>
          </div>
        </div>

        <!-- ===== PAGINATION ===== -->
        <div v-if="totalPages > 1" class="mt-10 flex flex-col sm:flex-row items-center justify-between gap-4">
          <!-- Items Count -->
          <p class="text-[#B9CACB] text-sm font-medium">
            {{ $t('Store.Showing') }} {{ itemStart }} - {{ itemEnd }} {{ $t('Store.Of') }} {{ totalItems }} {{
              $t('Store.Items') }}
          </p>

          <!-- Pagination Controls -->
          <div class="flex items-center gap-2">
            <button @click="prevPage()" :disabled="currentPage === 1"
              class="flex items-center justify-center w-9 h-9 rounded-[8px] bg-[#1A1D24] border border-gray-700/50 text-white disabled:opacity-30 hover:border-[#2EBBF0]/50 transition-colors">
              <svg class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
              </svg>
            </button>

            <span v-for="page in visiblePages" :key="page">
              <button @click="goToPage(page)" :class="[
                'w-9 h-9 rounded-[8px] font-bold text-sm transition-colors',
                currentPage === page
                  ? 'bg-[#2EBBF0] text-black'
                  : 'bg-[#1A1D24] border border-gray-700/50 text-white hover:border-[#2EBBF0]/50',
              ]">
                {{ page }}
              </button>
            </span>

            <button @click="nextPage()" :disabled="currentPage === totalPages"
              class="flex items-center justify-center w-9 h-9 rounded-[8px] bg-[#1A1D24] border border-gray-700/50 text-white disabled:opacity-30 hover:border-[#2EBBF0]/50 transition-colors">
              <svg class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>

  <StoreTransactionModal :show="showTransactionModal" :transactions="transactions" :store="selectedStore"
    @close="showTransactionModal = false" />

  <RedirectModal :showModal="showRedirectModal" :user="$store.state.user" :isUserLoggedIn="isUserLoggedIn"
    @close="showRedirectModal = false" />
</template>

<script>
import axios from "axios";
import RedirectModal from "@/components/RedirectModal.vue";
import StoreTransactionModal from "@/components/StoreTransactionModal.vue";

export default {
  components: {
    RedirectModal,
    StoreTransactionModal,
  },
  data() {
    return {
      activeCategory: "all",
      loading: false,
      points: 0,
      animatedPoints: 0,

      selectedStore: null,

      // Filters and sorting
      searchQuery: "",
      selectedSortOption: { label: this.$t("Store.Sort.lowToHigh"), value: "lowToHigh" },
      sortOptions: [
        { label: this.$t("Store.Sort.highToLow"), value: "highToLow" },
        { label: this.$t("Store.Sort.lowToHigh"), value: "lowToHigh" },
        { label: this.$t("Store.Sort.aToZ"), value: "aToZ" },
        { label: this.$t("Store.Sort.zToA"), value: "zToA" },
      ],
      selectedPointsRange: "",
      minPoints: "",
      maxPoints: "",
      showSortDropdown: false,

      // Pagination
      currentPage: 1,
      itemsPerPage: 8,

      // UI Modals
      showErrorModal: false,
      errorMessage: "",
      showSuccessModal: false,
      showConfirmModal: false,
      showRedirectModal: false,
      showTransactionModal: false,

      // Products & Transactions
      products: [],
      transactions: [],
      animatedTotalItems: 0,

      // Purchase
      selectedProductId: null,
      selectedProductName: "",
      selectedProductPoints: 0,

      // Fallback & Filters UI
      showFallback: true,
      showDesktopFilter: false,
      showMobileFilter: false,
    };
  },
  computed: {
    isUserLoggedIn() {
      return this.$store.state.user !== null && this.$store.state.isVerified;
    },
    filteredProducts() {
      let result = this.products.filter((p) => p.status === "active");

      // Match category to store type
      if (this.selectedStore === "kick") {
        result = result.filter((p) => p.category?.toLowerCase() === "kick");
      } else if (this.selectedStore === "21.com") {
        result = result.filter((p) => p.category?.toLowerCase() === "21.com" || p.category?.toLowerCase() === "21com");
      }

      // Optional search filtering
      if (this.searchQuery) {
        result = result.filter((p) =>
          p.title?.toLowerCase().includes(this.searchQuery.toLowerCase())
        );
      }

      if (this.minPoints !== "" || this.maxPoints !== "") {
        const min = this.minPoints !== "" ? Number(this.minPoints) : 0;
        const max = this.maxPoints !== "" ? Number(this.maxPoints) : Infinity;
        result = result.filter((p) => p.requiredPoints >= min && p.requiredPoints <= max);
      }

      return result;
    },
    paginatedProducts() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      return this.filteredProducts.slice(start, start + this.itemsPerPage);
    },
    totalItems() {
      return this.filteredProducts.length;
    },
    totalPages() {
      return Math.ceil(this.totalItems / this.itemsPerPage);
    },
    itemStart() {
      return this.totalItems === 0 ? 0 : (this.currentPage - 1) * this.itemsPerPage + 1;
    },
    itemEnd() {
      return Math.min(this.currentPage * this.itemsPerPage, this.totalItems);
    },
    visiblePages() {
      const pages = [];
      const start = Math.max(1, this.currentPage - 2);
      const end = Math.min(this.totalPages, this.currentPage + 2);
      for (let i = start; i <= end; i++) pages.push(i);
      return pages;
    },
  },
  watch: {
    "$route.query.type": {
      immediate: true,
      handler(type) {
        if (type === "kick" || type === "21.com") {
          this.selectedStore = type;
          this.fetchProducts();
        } else {
          this.selectedStore = null;
        }
      },
    },
    selectedSortOption(newVal) {
      const match = this.sortOptions.find((opt) => opt.value === newVal);
      this.selectedSortLabel = match ? match.label : "";
    },
    totalItems(newVal) {
      this.animateNumber("animatedTotalItems", newVal);
    },
  },
  methods: {
    chooseStore(type) {
      if (type === "kick" || type === "21.com") {
        this.$router.push({ path: "/store", query: { type } });
      }
    },
    async init() {
      const baseUrl = process.env.VUE_APP_BASE_API;
      this.loading = true;

      try {
        const token = localStorage.getItem("maherco-access-token");
        if (!token) return (this.showRedirectModal = true);

        const { data } = await axios.get(`${baseUrl}/api/users/me`, {
          headers: {
            Authorization: `Bearer ${token}`,
            "x-api-key": process.env.VUE_APP_X_API_KEY,
          },
        });

        const user = data?.user;
        if (user) {
          this.$store.commit("UPDATE_USER", user);
          this.$store.commit("UPDATE_ISVERIFIED", true);
        }

        if (!this.isUserLoggedIn) this.showRedirectModal = true;
      } catch (err) {
        console.error("User verification failed");
        this.$store.commit("UPDATE_USER", null);
        this.$store.commit("UPDATE_ISVERIFIED", false);
        this.showRedirectModal = true;
      } finally {
        this.loading = false;
      }
    },

    async fetchProducts() {
      this.loading = true;

      try {
        const apiCategory = this.selectedStore === "21.com" ? "21com" : this.selectedStore;
        const res = await axios.get(
          `${process.env.VUE_APP_BASE_API}/api/store/products?category=${apiCategory}`,
          {
            headers: {
              "x-api-key": process.env.VUE_APP_X_API_KEY,
            },
          }
        );
        this.products = res.data.products || [];
      } catch (e) {
        console.error("Product fetch failed:", e);
      } finally {
        this.loading = false;
      }
    },
    async fetchUserPoints() {
      const token = localStorage.getItem("maherco-access-token");
      if (!token) return this.showError("You must be logged in to view your points.");

      try {
        const { data } = await axios.get(
          `${process.env.VUE_APP_BASE_API}/api/users/me/points`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "x-api-key": process.env.VUE_APP_X_API_KEY,
            },
          }
        );

        this.points = data.points;
        this.animateValues();
      } catch (e) {
        console.error("Point fetch failed:", e);
      }
    },

    async fetchTransactions() {
      const token = localStorage.getItem("maherco-access-token");
      if (!token) return this.showError("User not authenticated.");

      try {
        const apiCategory = this.selectedStore === "21.com" ? "21com" : this.selectedStore;
        const categoryParam = apiCategory ? `?category=${apiCategory}` : "";
        const { data } = await axios.get(
          `${process.env.VUE_APP_BASE_API}/api/store/purchase/transactions${categoryParam}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "x-api-key": process.env.VUE_APP_X_API_KEY,
            },
          }
        );

        this.transactions = Array.isArray(data.purchaseTransactions)
          ? data.purchaseTransactions.sort(
            (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
          )
          : [];

        this.showTransactionModal = true;
      } catch (e) {
        console.error("Transaction fetch failed:", e);
      }
    },
    showError(msg) {
      this.errorMessage = msg;
      this.showErrorModal = true;
    },

    showSuccess(ticket) {
      this.ticket = ticket;
      this.showSuccessModal = true;
    },

    goToItemPage(id, selectedStore) {
      this.$router.push(`/store/${selectedStore}/${id}`);
    },

    animateValues() {
      this.animateNumber("animatedPoints", this.points);
    },

    animateNumber(variable, targetValue) {
      let start = this[variable] || 0;
      if (start === targetValue) return;

      const step = Math.max(1, Math.abs(targetValue - start) / 20);
      const decreasing = start > targetValue;

      const interval = setInterval(() => {
        start = decreasing ? start - step : start + step;
        if (
          (decreasing && start <= targetValue) ||
          (!decreasing && start >= targetValue)
        ) {
          start = targetValue;
          clearInterval(interval);
        }
        this[variable] = Math.round(start).toLocaleString();
      }, 50);
    },

    // UI Toggles
    toggleDesktopFilter() {
      this.showDesktopFilter = !this.showDesktopFilter;
    },
    toggleMobileFilter() {
      this.showMobileFilter = !this.showMobileFilter;
    },
    toggleSortDropdown() {
      this.showSortDropdown = !this.showSortDropdown;
    },

    // Pagination
    applyFilter() {
      this.currentPage = 1;
    },
    scrollToTop() {
      const container = document.getElementById("main-scroll-container");
      if (container) {
        container.scrollTo({ top: 0, behavior: "smooth" });
      } else {
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
    },
    goToPage(page) {
      if (page >= 1 && page <= this.totalPages) {
        this.currentPage = page;
        this.scrollToTop();
      }
    },
    prevPage() {
      if (this.currentPage > 1) {
        this.currentPage--;
        this.scrollToTop();
      }
    },
    nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage++;
        this.scrollToTop();
      }
    },
    prevActivePage() {
      this.prevPage();
    },
    nextActivePage() {
      this.nextPage();
    },

    // Sorting
    applySort(option) {
      this.selectedSortOption = option;
      this.sortProducts(option.value);
      this.showSortDropdown = false;
    },
    sortProducts(order) {
      if (order === "highToLow") {
        this.products.sort((a, b) => b.requiredPoints - a.requiredPoints);
      } else if (order === "lowToHigh") {
        this.products.sort((a, b) => a.requiredPoints - b.requiredPoints);
      } else if (order === "aToZ") {
        this.products.sort((a, b) => a.title.localeCompare(b.title));
      } else if (order === "zToA") {
        this.products.sort((a, b) => b.title.localeCompare(a.title));
      }
    },

    refreshPage() {
      this.fetchProducts();
      this.fetchUserPoints();
    },
  },
  mounted() {
    this.init();
    this.fetchUserPoints();
  },
};
</script>

<style>
/* Smooth fade transition */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease-in-out, transform 0.3s ease-in-out;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
  transform: translateY(-5px);
}

.loader {
  display: inline-block;
  width: 24px;
  height: 24px;
  border: 3px solid white;
  border-radius: 50%;
  border-top-color: transparent;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(360deg);
  }
}


.responsive-grid-xl {
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
}

@media (min-width: 1280px) {
  .responsive-grid-xl {
    grid-template-columns: repeat(auto-fit, minmax(250px, 0fr));
  }
}

@keyframes twist-spin {
  0% {
    transform: rotate(0deg);
  }

  100% {
    transform: rotate(360deg);
  }
}

.twist-spinner {
  width: 3rem;
  height: 3rem;
  border-radius: 50%;
  border: 3px solid rgba(46, 187, 240, 0.15);
  border-top-color: #2ebbf0;
  border-bottom-color: #beff2e;
  animation: twist-spin 1s cubic-bezier(0.68, -0.55, 0.265, 1.55) infinite;
}

.twist-spinner-small {
  width: 1.25rem;
  height: 1.25rem;
  border-radius: 50%;
  border: 2px solid rgba(46, 187, 240, 0.15);
  border-top-color: #2ebbf0;
  border-bottom-color: #beff2e;
  animation: twist-spin 1s cubic-bezier(0.68, -0.55, 0.265, 1.55) infinite;
}

.twist-spinner-big {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  border: 4px solid rgba(46, 187, 240, 0.15);
  border-top-color: #2ebbf0;
  border-bottom-color: #beff2e;
  animation: twist-spin 1s cubic-bezier(0.68, -0.55, 0.265, 1.55) infinite;
}
</style>
